import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openModal } = useOutletContext();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);

        // Fetch related posts
        const allPostsRes = await axios.get(`${API}/blog`);
        const related = allPostsRes.data
          .filter(p => p.slug !== slug && p.category === response.data.category)
          .slice(0, 3);
        setRelatedPosts(related);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      }
    };
    fetchPost();
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="pt-20" data-testid="blog-detail-page">
      {/* Back Button */}
      <div className="bg-white py-4 border-b">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center space-x-2 text-[#666] hover:text-[#E12429] transition-colors"
            data-testid="back-to-blog-button"
          >
            <ArrowLeft size={20} />
            <span>Back to Blog</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-[60vh]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${post.image})` }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center text-white">
              <span className="px-4 py-2 bg-[#E12429] text-white text-sm font-semibold rounded-full inline-block mb-4">
                {post.category}
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">{post.title}</h1>
              <div className="flex items-center justify-center space-x-6 text-sm text-white/90">
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar size={16} />
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Article Content */}
            <div className="lg:col-span-2">
              <div className="prose prose-lg max-w-none">
                <div className="text-xl text-[#666] mb-8 leading-relaxed">
                  {post.excerpt}
                </div>
                <div className="text-[#444] leading-relaxed whitespace-pre-line">
                  {post.content}
                </div>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-[#F5F5F5] text-[#666] text-sm rounded-full flex items-center space-x-1"
                    >
                      <Tag size={12} />
                      <span>{tag}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              {/* CTA */}
              <div className="bg-[#E12429] text-white p-6 rounded-lg mb-8">
                <h3 className="text-xl font-bold mb-3">Ready to Transform Your Workspace?</h3>
                <p className="text-sm mb-4">Book a tour of our premium locations and experience The Circuit difference.</p>
                <button
                  onClick={() => openModal()}
                  className="w-full bg-white text-[#E12429] font-semibold py-3 rounded-lg hover:bg-gray-100 transition-all"
                  data-testid="book-tour-button"
                >
                  Book a Tour
                </button>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div>
                  <h3 className="text-xl font-bold text-[#222] mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {relatedPosts.map((related) => (
                      <Link
                        key={related.id}
                        to={`/blog/${related.slug}`}
                        className="block group"
                      >
                        <div className="flex space-x-3">
                          <img
                            src={related.image}
                            alt={related.title}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                          <div>
                            <h4 className="font-semibold text-[#222] text-sm mb-1 group-hover:text-[#E12429] transition-colors line-clamp-2">
                              {related.title}
                            </h4>
                            <p className="text-xs text-[#666]">{formatDate(related.created_at)}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}